import React, { useState, useEffect, useCallback } from 'react';
import { Contract, JsonRpcProvider } from 'ethers';
import './EventAnalytics.css';

// NO MOCK DATA - All analytics from real blockchain data only

const EventAnalytics = ({ event, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    totalTicketsSold: 0,
    totalRevenue: 0,
    ticketTypeStats: [],
    recentSales: [],
    salesByDay: [],
    attendanceRate: 0,
    checkedInCount: 0,
  });
  const [timeRange, setTimeRange] = useState('all'); // 'all', '7d', '30d', '90d'

  const fetchBlockchainData = useCallback(async () => {
    try {
      const RPC_URL = process.env.REACT_APP_BASE_RPC_URL || 'https://sepolia.base.org';
      const provider = new JsonRpcProvider(RPC_URL);

      const LEGACY_ABI = [
        'function totalSupplyByType(uint256) view returns (uint256)',
        'function ticketTypes(uint256) view returns (uint256 price, uint256 maxSupply, uint256 saleStart, uint256 saleEnd, uint256 transferLockStart, uint256 transferLockEnd, string metadataURI)',
      ];
      const GET_TT_ABI = [
        'function getTicketType(uint256) view returns (uint256 eventId, string name, uint256 price, uint256 maxSupply, uint256 sold, uint256 available, uint256 saleStart, uint256 saleEnd, string metadataURI)',
      ];

      const legacyContract = new Contract(event.contractAddress, LEGACY_ABI, provider);
      const enrichedContract = new Contract(event.contractAddress, GET_TT_ABI, provider);

      // Use event.ticketTypes IDs if available, otherwise scan 1-10
      const idsToCheck = event.ticketTypes?.length > 0
        ? event.ticketTypes.map(t => Number(t.id))
        : Array.from({ length: 10 }, (_, i) => i + 1);

      const ticketTypeStats = [];
      let totalSold = 0;
      let totalRevenue = 0;

      for (const id of idsToCheck) {
        try {
          let name = '', price, maxSupply, sold;

          try {
            // EventTicketWithEvents.getTicketType → (eventId, name, price, maxSupply, sold, available, saleStart, saleEnd, metadataURI)
            const tt = await enrichedContract.getTicketType(id);
            name = tt[1] || '';
            price = Number(tt[2]?.toString() || '0');
            maxSupply = Number(tt[3]?.toString() || '0');
            sold = Number(tt[4]?.toString() || '0');
          } catch {
            // Legacy fallback
            const ticketType = await legacyContract.ticketTypes(id);
            price = Number(ticketType[0]?.toString() || '0');
            maxSupply = Number(ticketType[1]?.toString() || '0');
            try { sold = Number((await legacyContract.totalSupplyByType(id)).toString()); } catch { sold = 0; }
          }

          if (maxSupply <= 0) continue;

          const priceDisplay = price / 100; // PGT has 2 decimals
          const revenue = sold * priceDisplay;

          totalSold += sold;
          totalRevenue += revenue;

          const eventTicketType = event.ticketTypes?.find(t => Number(t.id) === id);
          const displayName = eventTicketType?.name || name || `Ticket Type ${id}`;

          ticketTypeStats.push({
            id,
            name: displayName,
            price: priceDisplay,
            maxSupply,
            sold,
            revenue,
            percentageSold: (sold / maxSupply) * 100,
          });
        } catch {
          continue;
        }
      }

      setAnalytics({
        totalTicketsSold: totalSold,
        totalRevenue: totalRevenue.toFixed(2),
        ticketTypeStats,
        recentSales: [],
        salesByDay: [],
        attendanceRate: 0,
        checkedInCount: 0,
      });
    } catch (error) {
      console.error('Error fetching blockchain data:', error);
      setAnalytics({
        totalTicketsSold: 0,
        totalRevenue: '0.00',
        ticketTypeStats: [],
        recentSales: [],
        salesByDay: [],
        attendanceRate: 0,
        checkedInCount: 0,
      });
    }
  }, [event.contractAddress]);

  const fetchBackendAnalytics = useCallback(async () => {
    // Only attempt if API_BASE is explicitly configured (avoids CORS/404 on production domains)
    const API_BASE = process.env.REACT_APP_API_URL;
    if (!API_BASE) return;

    try {
      const response = await fetch(`${API_BASE}/api/events/${event.id}/analytics?range=${timeRange}`);
      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await response.json();
          setAnalytics(prev => ({ ...prev, ...data }));
        }
      }
    } catch {
      // Backend analytics optional — blockchain data is primary source
    }
  }, [event.id, timeRange]);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      // In production, fetch from blockchain and backend
      await fetchBlockchainData();
      await fetchBackendAnalytics();
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchBlockchainData, fetchBackendAnalytics]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const exportAnalytics = () => {
    const dataStr = JSON.stringify(analytics, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.name}-analytics-${new Date().toISOString()}.json`;
    link.click();
  };

  const maxSales = Math.max(...analytics.salesByDay.map(d => d.sales), 1);

  return (
    <div className="event-analytics-container">
      <div className="analytics-header">
        <button className="btn-back" onClick={onBack}>
          ← Back to Events
        </button>
        <div className="header-info">
          <h1>{event.name} - Analytics</h1>
          <p>Comprehensive event performance metrics</p>
        </div>
        <button className="btn-export" onClick={exportAnalytics}>
          📊 Export Data
        </button>
      </div>

      <div className="time-range-selector">
        <button
          className={timeRange === 'all' ? 'active' : ''}
          onClick={() => setTimeRange('all')}
        >
          All Time
        </button>
        <button
          className={timeRange === '7d' ? 'active' : ''}
          onClick={() => setTimeRange('7d')}
        >
          Last 7 Days
        </button>
        <button
          className={timeRange === '30d' ? 'active' : ''}
          onClick={() => setTimeRange('30d')}
        >
          Last 30 Days
        </button>
        <button
          className={timeRange === '90d' ? 'active' : ''}
          onClick={() => setTimeRange('90d')}
        >
          Last 90 Days
        </button>
      </div>

      {loading ? (
        <div className="analytics-loading">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      ) : (
        <>
          <div className="analytics-overview">
            <div className="stat-card-large">
              <div className="stat-icon">🎟️</div>
              <div className="stat-content">
                <div className="stat-value">{analytics.totalTicketsSold}</div>
                <div className="stat-label">Total Tickets Sold</div>
              </div>
            </div>

            <div className="stat-card-large">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <div className="stat-value">{analytics.totalRevenue} PGT</div>
                <div className="stat-label">Total Revenue</div>
              </div>
            </div>

            <div className="stat-card-large">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <div className="stat-value">{analytics.checkedInCount}</div>
                <div className="stat-label">Checked In</div>
              </div>
            </div>

            <div className="stat-card-large">
              <div className="stat-icon">📈</div>
              <div className="stat-content">
                <div className="stat-value">{analytics.attendanceRate.toFixed(1)}%</div>
                <div className="stat-label">Attendance Rate</div>
              </div>
            </div>
          </div>

          <div className="analytics-grid">
            <div className="analytics-section sales-chart">
              <h2>Sales Over Time</h2>
              <div className="chart-container">
                <div className="chart-bars">
                  {analytics.salesByDay.map((day, index) => (
                    <div key={index} className="chart-bar-wrapper">
                      <div
                        className="chart-bar"
                        style={{ height: `${(day.sales / maxSales) * 100}%` }}
                      >
                        <span className="bar-value">{day.sales}</span>
                      </div>
                      <div className="bar-label">{day.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="analytics-section ticket-types">
              <h2>Ticket Type Breakdown</h2>
              <div className="ticket-types-stats">
                {analytics.ticketTypeStats.map((type) => (
                  <div key={type.id} className="ticket-type-stat">
                    <div className="ticket-stat-header">
                      <h3>{type.name}</h3>
                      <div className="ticket-stat-price">{type.price} PGT</div>
                    </div>
                    <div className="ticket-stat-progress">
                      <div className="progress-bar-bg">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${type.percentageSold}%` }}
                        />
                      </div>
                      <div className="progress-label">
                        {type.sold} / {type.maxSupply} sold ({type.percentageSold.toFixed(1)}%)
                      </div>
                    </div>
                    <div className="ticket-stat-revenue">
                      Revenue: <strong>{type.revenue.toFixed(2)} PGT</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="analytics-section recent-sales">
            <h2>Recent Sales</h2>
            {analytics.recentSales.length === 0 ? (
              <div className="no-sales">
                <p>No sales yet</p>
              </div>
            ) : (
              <div className="sales-table">
                <div className="sales-table-header">
                  <div>Buyer</div>
                  <div>Ticket Type</div>
                  <div>Quantity</div>
                  <div>Amount</div>
                  <div>Time</div>
                </div>
                {analytics.recentSales.map((sale) => (
                  <div key={sale.id} className="sales-table-row">
                    <div className="sale-buyer">{sale.buyer}</div>
                    <div className="sale-type">{sale.ticketType}</div>
                    <div className="sale-qty">{sale.quantity}</div>
                    <div className="sale-amount">{sale.amount} PGT</div>
                    <div className="sale-time">
                      {sale.timestamp.toLocaleTimeString()} - {sale.timestamp.toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="analytics-section event-details">
            <h2>Event Details</h2>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-label">Event Name</div>
                <div className="detail-value">{event.name}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Event Date</div>
                <div className="detail-value">{new Date(event.date).toLocaleString()}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Contract Address</div>
                <div className="detail-value contract-address">{event.contractAddress}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Chain ID</div>
                <div className="detail-value">{event.chainId}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Status</div>
                <div className="detail-value">
                  <span className={`status-badge ${event.active ? 'active' : 'inactive'}`}>
                    {event.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EventAnalytics;
